import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarBlankIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CheckIcon,
  LockSimpleIcon,
  WarningIcon,
} from '@phosphor-icons/react'
import moment from 'moment'
import { CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { Modal } from 'app/components/Modal'
import { PageScroll } from 'app/components/layout/PageScroll'
import { Button } from 'app/components/ui/button'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import { cn } from 'app/lib/utils'
import type { PmsGate } from 'app/types/pms'
import { DayColumn, PlacedOpening } from './scheduleDay'
import { formatMinutes, formatRange } from 'lib/dayClock'
import { ScheduleDay } from './useScheduleDay'

/** An opening has to stay big enough to carry its time and take a click. */
const MIN_OPENING_PX = 32
/** Nothing shorter reads as a block, however short the appointment. */
const MIN_BLOCK_PX = 14
const MAX_PRACTICE_BUTTONS_BEFORE_DROPDOWN = 10

/**
 * The board fills the height it is given, so a quiet day is the same size as a
 * busy one and the two can be compared at a glance.
 */
function band(start: number, end: number, startMinute: number, endMinute: number, minPx: number) {
  const span = Math.max(endMinute - startMinute, 1)
  return {
    top: `${((start - startMinute) / span) * 100}%`,
    height: `${((end - start) / span) * 100}%`,
    minHeight: minPx,
  }
}

const NO_EXCLUDED_LOCATIONS: string[] = []

type Props = {
  day: ScheduleDay
  locationName?: string
  locations: { id: string; name: string }[]
  excludedLocations?: string[]
  pmsGate?: PmsGate
  locationId?: string
  /** Still working out which locations this workspace has. */
  loadingLocations?: boolean
  /**
   * Several practices in this workspace and none chosen yet. The board asks
   * before it draws, because the alternative is drawing whichever location came
   * back first and calling it theirs.
   */
  asking?: boolean
  onLocation: (id: string) => void
  onSelectOpening: (date: string, placed: PlacedOpening, room: string) => void
}

function hourMarks(startMinute: number, endMinute: number): number[] {
  const marks: number[] = []
  for (let minute = startMinute; minute <= endMinute; minute += 60) marks.push(minute)
  return marks
}

function hours(minutes: number): string {
  const whole = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (!whole) return `${rest}m`
  return rest ? `${whole}h ${rest}m` : `${whole}h`
}

/**
 * A restorative appointment's description is a list of tooth work
 * ("#15-FPC, #15-BU, #15-Seat"), which ran wider than the board on one line.
 * Split on the practice's own commas.
 */
function AppointmentTip({
  start,
  end,
  description,
  confirmed,
}: {
  start: number
  end: number
  description?: string | null
  confirmed?: boolean | null
}) {
  const parts = (description ?? ``)
    .split(`,`)
    .map((part) => part.trim())
    .filter(Boolean)
  return (
    <span className="block text-left">
      <span className="block font-semibold">{formatRange(start, end)}</span>
      {confirmed && <span className="mt-0.5 block">Confirmed</span>}
      {parts.length === 1 && <span className="mt-0.5 block opacity-90">{parts[0]}</span>}
      {parts.length > 1 && (
        <span className="mt-1 block space-y-0.5">
          {parts.map((part) => (
            <span key={part} className="block opacity-90">
              · {part}
            </span>
          ))}
        </span>
      )}
    </span>
  )
}

function roomStory(column: DayColumn): string {
  const parts = [column.label]
  if (column.state === `closed`) parts.push(`not working today`)
  else if (column.state === `unknown`) parts.push(`hours not published for today, so open time is not known`)
  else if (column.state === `full`) parts.push(`every hour sold`)
  else parts.push(`${hours(column.freeMinutes)} free of ${hours(column.workingMinutes)}`)
  if (column.bookedMinutes > 0) parts.push(`${hours(column.bookedMinutes)} booked`)
  return parts.join(` · `)
}

/**
 * One room, named as the practice management system names it. Booked work is
 * the ground and openings are the figure, the opposite weighting to a PMS day
 * view: staff can already read their booked day in their own software.
 */
function Room({
  column,
  startMinute,
  endMinute,
  date,
  onSelectOpening,
}: {
  column: DayColumn
  startMinute: number
  endMinute: number
  date: string
  onSelectOpening: (date: string, placed: PlacedOpening, room: string) => void
}) {
  const isOpen = column.state === `open`
  // Only `closed` is hatched. An `unknown` room holds real work, so striking it
  // through would say the practice is shut when it is not.
  const isHatched = column.state === `closed`

  return (
    <div className="flex min-w-23 flex-1 flex-col md:min-w-0">
      {/* Truncated rather than wrapped: wrapping pushed every column down by the
          length of the worst room name. */}
      <TooltipWrapper message={roomStory(column)} className="block">
        <p
          className={cn(
            `truncate pb-1 text-center text-[10px] font-semibold leading-tight`,
            isOpen ? `text-emerald-900` : `text-subtle`,
          )}
        >
          {column.label}
        </p>
      </TooltipWrapper>

      <div
        className={cn(
          `relative min-h-0 min-w-0 flex-1 overflow-hidden rounded-lg border transition`,
          // White inside, so the green belongs to the blocks that can be sold
          // rather than to the whole column.
          isOpen
            ? `border-emerald-300 bg-surface shadow-[0_0_0_1px_color-mix(in_oklab,var(--color-emerald-500)_12%,transparent)]`
            : `bg-surface`,
          !isOpen && !isHatched && `border-default`,
          isHatched &&
            `border-dashed border-default bg-[repeating-linear-gradient(45deg,transparent,transparent_6px,color-mix(in_oklab,var(--fg-strong)_3.5%,transparent)_6px,color-mix(in_oklab,var(--fg-strong)_3.5%,transparent)_12px)]`,
        )}
      >
        {hourMarks(startMinute, endMinute).map((minute) => (
          <div
            key={minute}
            className="pointer-events-none absolute inset-x-0 border-t border-subtle"
            style={{ top: `${((minute - startMinute) / Math.max(endMinute - startMinute, 1)) * 100}%` }}
          />
        ))}

        {/* Booked work, in the room the practice management system put it in. */}
        {column.appointments.map((block) => (
          <div
            key={block.appointment.id}
            className="absolute px-px"
            style={{
              ...band(block.start, block.end, startMinute, endMinute, MIN_BLOCK_PX),
              left: `${(block.lane / block.lanes) * 100}%`,
              width: `${(1 / block.lanes) * 100}%`,
            }}
          >
            <TooltipWrapper
              className="block h-full w-full"
              // Above the block and right-aligned, so the tip sits clear of the
              // work below it rather than covering the next appointment down.
              placement="top-end"
              tooltipClassName="max-w-[220px] whitespace-normal"
              message={
                <AppointmentTip
                  start={block.start}
                  end={block.end}
                  description={block.appointment.description}
                  confirmed={block.appointment.confirmed}
                />
              }
            >
              <div className="h-full w-full overflow-hidden rounded border border-default/80 bg-surface-muted/90 px-1 py-0.5">
                <span className="block truncate text-[9px] font-medium leading-tight text-subtle">
                  {block.appointment.confirmed && (
                    <CheckIcon weight="bold" aria-label="Confirmed" className="mr-0.5 inline size-2.5 text-green-700" />
                  )}
                  {block.appointment.description || `Booked`}
                </span>
              </div>
            </TooltipWrapper>
          </div>
        ))}

        {/* The openings, drawn last so they sit above the booked work. */}
        {column.openings.map((placed) => (
          <div
            key={placed.start}
            className="absolute inset-x-0.5 z-10"
            style={band(placed.start, placed.end, startMinute, endMinute, MIN_OPENING_PX)}
          >
            <TooltipWrapper
              className="block h-full w-full"
              placement="top-end"
              tooltipClassName="max-w-[220px] whitespace-normal"
              message={
                <span className="block text-left">
                  <span className="block font-semibold">{formatRange(placed.start, placed.end)}</span>
                  <span className="mt-0.5 block opacity-90">{column.label}</span>
                </span>
              }
            >
              {/* Tinted at rest, saturated on hover: a fully open day was a wall
                  of solid green, which read as an alert rather than an offer. */}
              <Button
                type="button"
                variant="icon"
                size="icon"
                onClick={() => onSelectOpening(date, placed, column.label)}
                // oxlint-disable-next-line shadcn/no-restyle, shadcn/no-raw-colors, shadcn/no-arbitrary-values -- emerald marks an open schedule slot
                className="h-full w-full rounded-md bg-emerald-100 px-1 text-emerald-900 ring-1 ring-inset ring-emerald-400 hover:bg-emerald-200 hover:text-emerald-950 hover:shadow-[0_2px_8px_color-mix(in_oklab,var(--color-emerald-500)_25%,transparent)] hover:ring-emerald-500"
              >
                {/* Both ends: a start time alone does not say whether the chair
                    is free for half an hour or two. */}
                <span className="truncate text-[10px] font-semibold leading-none tracking-tight">
                  {formatRange(placed.start, placed.end)}
                </span>
              </Button>
            </TooltipWrapper>
          </div>
        ))}
      </div>
    </div>
  )
}

function Stat({
  value,
  label,
  tone,
  loading,
}: {
  value: string | number
  label: string
  tone?: `open` | `plain`
  loading?: boolean
}) {
  return (
    <div
      className={cn(
        `rounded-xl border px-3 py-2.5 md:px-4 md:py-3`,
        tone === `open` ? `border-emerald-200 bg-emerald-50` : `border-default bg-surface`,
      )}
    >
      {loading ? (
        <span className="block h-7 w-16 animate-pulse rounded bg-surface-muted" />
      ) : (
        <p
          className={cn(
            `gilroy text-xl leading-none md:text-2xl`,
            tone === `open` ? `text-emerald-700` : `text-strong`,
          )}
        >
          {value}
        </p>
      )}
      <p className="mt-1 text-body-xs leading-tight text-subtle">{label}</p>
    </div>
  )
}

/**
 * The board's own shape while it loads, rather than a spinner.
 *
 * Reading a day takes a couple of seconds against the practice management
 * system, and an empty frame with a clock down the side reads as a practice with
 * no rooms. Columns of the right size, pulsing, say the answer is on its way and
 * put the eye where the rooms will be.
 */
function SkeletonBoard({ columns }: { columns: number }) {
  return (
    <>
      {Array.from({ length: columns }, (_, index) => (
        <div key={index} className="flex min-w-23 flex-1 flex-col md:min-w-0">
          <span className="mx-auto mb-1 h-2.5 w-3/4 animate-pulse rounded bg-surface-muted" />
          <div className="relative min-h-0 flex-1 overflow-hidden rounded-lg border border-subtle bg-surface p-1">
            {/* A couple of blocks at different heights, so it reads as a day
                with work in it rather than as a loading bar. */}
            <span
              className="absolute inset-x-1 animate-pulse rounded bg-surface-muted"
              style={{ top: `${8 + index * 4}%`, height: `${18 + (index % 3) * 8}%` }}
            />
            <span
              className="absolute inset-x-1 animate-pulse rounded bg-surface-muted"
              style={{ top: `${44 + (index % 2) * 6}%`, height: `${14 + (index % 4) * 6}%` }}
            />
          </div>
        </div>
      ))}
    </>
  )
}

/**
 * The practice's day, one column per room.
 *
 * Read only. Clicking an opening asks who to put in it; nothing here writes to
 * the practice's calendar.
 */
export function ScheduleDayComponent({
  day,
  locationName,
  locations,
  excludedLocations = NO_EXCLUDED_LOCATIONS,
  pmsGate,
  locationId,
  loadingLocations,
  asking,
  onLocation,
  onSelectOpening,
}: Props) {
  const board = day.day
  const when = moment(board.date)
  const isToday = when.isSame(moment(), `day`)
  /**
   * Still working, whichever half is working.
   *
   * The day hook has nothing to fetch until a location is chosen, so between
   * the page opening and the locations arriving it reports itself idle with no
   * rooms. Reading that as loaded flashed a board of zeroes before the real
   * numbers landed.
   * While the question is on screen nothing has been fetched, so the board is
   * not idle and empty, it is waiting on an answer.
   */
  const busy = day.loading || Boolean(loadingLocations) || Boolean(asking)
  // No location here can draw a schedule, so there is no board to show under the message.
  const unavailable =
    !locationId &&
    !loadingLocations &&
    !asking &&
    excludedLocations.length > 0 &&
    (pmsGate === `padlock` || pmsGate === `hidden`)
  // A room the practice is not running is drawn hatched and says so on hover.
  // It used to be counted in a line under the stats as well, which told a
  // practice something they already know about their own roster.

  return (
    <PageScroll contentClassName="flex h-full flex-col px-0 pb-6 pt-0 md:px-10 md:pt-8">
      <div className="mx-auto w-full max-w-6xl shrink-0">
        <Link
          to="/action-center"
          className="inline-flex items-center gap-1.5 text-body-sm text-subtle transition hover:text-strong"
        >
          <ArrowLeftIcon size={13} weight="bold" /> Action Center
        </Link>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
          <h3 className="gilroy flex items-center gap-3 text-strong max-md:text-heading-lg">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
              <CalendarBlankIcon size={20} weight="bold" />
            </span>
            Open chair time
          </h3>

          <div className="flex w-full flex-wrap items-center gap-2 md:w-auto">
            {/* Off while the modal is asking. It is unreachable behind the
                backdrop anyway, and two practice pickers on one screen is one
                too many for anybody reading the page rather than looking at
                it. */}
            {locations.length > 1 && !asking && (
              <select
                value={locationId ?? ``}
                onChange={(event) => onLocation(event.target.value)}
                className="w-full rounded-lg border border-default bg-surface px-3 py-2.5 text-body-sm text-strong md:w-auto md:py-1.5"
              >
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            )}
            <div className="flex flex-1 items-center justify-between gap-1 rounded-lg border border-default bg-surface p-0.5 md:flex-none md:justify-start">
              <Button
                type="button"
                variant="subtle"
                size="icon-sm"
                className="size-10 md:size-7"
                onClick={() => day.goToDate(when.clone().subtract(1, `day`).format(`YYYY-MM-DD`))}
                aria-label="Previous day"
              >
                <CaretLeftIcon size={13} weight="bold" />
              </Button>
              <span className="px-1 text-body-sm tabular-nums text-strong">{when.format(`ddd D MMM`)}</span>
              <Button
                type="button"
                variant="subtle"
                size="icon-sm"
                className="size-10 md:size-7"
                onClick={() => day.goToDate(when.clone().add(1, `day`).format(`YYYY-MM-DD`))}
                aria-label="Next day"
              >
                <CaretRightIcon size={13} weight="bold" />
              </Button>
            </div>
            {/* Always here, not only when you have wandered off. A control that
                appears once you are lost is one you have to notice mid-search;
                one that is always in the same place is somewhere to aim. */}
            <Button
              type="button"
              variant="field"
              size="sm"
              className="h-11 md:h-8"
              onClick={() => day.goToDate(moment().format(`YYYY-MM-DD`))}
              disabled={isToday}
            >
              Today
            </Button>
          </div>
        </div>

        {excludedLocations.length > 0 && locations.length > 0 && !loadingLocations && (
          <p className="mt-2 text-body-xs text-subtle">
            Not shown: {excludedLocations.join(`, `)}. Only locations on Open Dental, Dentrix or Eaglesoft have a
            schedule here.
          </p>
        )}

        {unavailable ? (
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-surface-muted px-3 py-2.5">
            <span
              aria-hidden
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-strong text-subtle"
            >
              {pmsGate === `padlock` ? <LockSimpleIcon size={16} /> : <CalendarBlankIcon size={16} />}
            </span>
            <span className="text-body-sm text-default">
              {pmsGate === `padlock`
                ? `Connect a practice management system to see your chairs here.`
                : `The schedule comes from Open Dental, Dentrix or Eaglesoft. This workspace's practice management system doesn't share it with Trackable.`}
            </span>
          </div>
        ) : !locationId && !loadingLocations && !asking ? (
          /* Nothing to ask about is not the same as nothing to say. Without
             this the board sat on "…" and "rooms working of 0" for ever, which
             reads as a page still loading rather than one that has no location
             to draw.
             Gated on the locations having finished loading, because for the
             first moment of every page load there is no location yet, and this
             banner was flashing up on a workspace that has one. */
          <p className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-body-sm leading-relaxed text-amber-800">
            <WarningIcon size={13} weight="fill" className="mr-1 inline" />
            No locations came back for this workspace, so there is no chair time to show. Every location with a practice
            management system behind it can be drawn here.
          </p>
        ) : day.failed && !asking ? (
          <p className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-body-sm leading-relaxed text-amber-800">
            <WarningIcon size={13} weight="fill" className="mr-1 inline" />
            The scheduling engine could not be reached, so this is not an empty day. Nothing below says what is open.
          </p>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-3 gap-2 md:gap-3">
              {/* Hours, not slots. The practice thinks in chair time they have
                  not sold, and one four hour gap and four one hour gaps are the
                  same money. */}
              <Stat
                value={board.hoursMissing && !board.freeMinutes ? `?` : hours(board.freeMinutes)}
                label={board.hoursMissing ? `of open chair time known` : `of open chair time`}
                tone="open"
                loading={busy}
              />
              <Stat
                value={board.totalBooked}
                label={board.totalBooked === 1 ? `appointment booked` : `appointments booked`}
                loading={busy}
              />
              <Stat
                value={board.workingCount}
                label={day.roomCount ? `rooms working of ${day.roomCount}` : `rooms working`}
                loading={busy}
              />
            </div>

            {/* Where the limit actually is, stated rather than hidden. A room no
                availability record covers can never show an opening however
                empty the chair is, and reading those columns as full would say
                the day is busier than it is. */}
            {/* A day with nothing to sell is a dead end otherwise, clicked out
                of one day at a time. The engine already answered for the days
                after this one, so the way forward is in hand before anybody
                asks for it. */}
            {/* Said before the empty-day line below, because "nothing to fill"
                would be a conclusion drawn from hours we do not have. Measured
                at Alamo City Smiles: thirty appointments in the day and no
                published hours for it at all. */}
            {!busy && board.hoursMissing && (
              <p className="mt-3 text-body-sm leading-relaxed text-amber-800">
                <WarningIcon size={13} weight="fill" className="mr-1 inline" />
                This practice has not published its room hours for this day, so the open time above is only what is
                known. The booked work below is real.
              </p>
            )}

            {!busy && !board.hoursMissing && board.totalOpenings === 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {day.nextOpening ? (
                  <>
                    <p className="text-body-sm text-subtle">Nothing to fill on this day.</p>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => day.goToDate(day.nextOpening!)}
                      // oxlint-disable-next-line shadcn/no-restyle, shadcn/no-raw-colors -- emerald marks an open schedule slot
                      className="gap-1.5 rounded-lg bg-emerald-600 px-3 text-body-sm text-on-color hover:bg-emerald-700"
                    >
                      Next opening: {moment(day.nextOpening).format(`ddd D MMM`)}
                      <ArrowRightIcon size={13} weight="bold" />
                    </Button>
                  </>
                ) : (
                  <p className="text-body-sm text-subtle">
                    Nothing to fill on this day, and nothing open
                    {day.horizon ? ` through ${moment(day.horizon).format(`D MMMM`)}` : ` in the days ahead`}, which is
                    as far as the scheduler looks.
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {day.pmsUnavailable && (
          <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-body-sm leading-relaxed text-amber-800">
            <WarningIcon size={13} weight="fill" className="mr-1 inline" />
            The practice management system did not answer, so the booked work is missing rather than absent. The rooms
            below are not empty.
          </p>
        )}

        {day.hoursUnavailable && (
          <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-body-sm leading-relaxed text-amber-800">
            <WarningIcon size={13} weight="fill" className="mr-1 inline" />
            The rooms' working hours could not be read, so time that looks closed below may simply be unknown.
          </p>
        )}
      </div>

      {!unavailable && (
        <div className="mt-5 flex min-h-[490px] flex-1 flex-col md:-mx-10 md:min-h-0">
          <div className="soft-scrollbar mx-auto flex min-h-0 w-full max-w-[1700px] flex-1 flex-col overflow-x-auto pb-2 md:px-10">
            <div
              className="flex min-h-[460px] flex-1 gap-1.5 md:min-w-(--board-min-width)"
              // Wide enough for a room name on one line. A fourteen-room practice
              // overflows a laptop and scrolls sideways, which is the right trade:
              // a truncated column name is worth less than a legible one.
              // While loading, the skeleton's own column count keeps the board the
              // width it is about to be, so nothing jumps sideways when the rooms
              // arrive.
              // On a phone each room holds its own minimum instead and the board swipes.
              style={
                {
                  '--board-min-width': `${Math.max((board.columns.length || day.roomCount || 5) * 112 + 48, 700)}px`,
                } as CSSProperties
              }
            >
              {/* The clock, once, down the left. The padding clears the one-line
                room names above; there is nothing below the columns now. */}
              <div className="sticky left-0 z-20 flex w-12 shrink-0 flex-col bg-surface pt-[18px] md:static md:bg-transparent">
                <div className="relative min-h-0 flex-1">
                  {hourMarks(board.startMinute, board.endMinute).map((minute) => (
                    <span
                      key={minute}
                      className="absolute right-1 -translate-y-1/2 text-[10px] tabular-nums text-minimal"
                      style={{
                        top: `${((minute - board.startMinute) / Math.max(board.endMinute - board.startMinute, 1)) * 100}%`,
                      }}
                    >
                      {formatMinutes(minute).replace(`:00`, ``)}
                    </span>
                  ))}
                </div>
              </div>

              {busy && !board.columns.length && <SkeletonBoard columns={day.roomCount || 5} />}

              {board.columns.map((column) => (
                <Room
                  key={column.operatoryId}
                  column={column}
                  date={board.date}
                  startMinute={board.startMinute}
                  endMinute={board.endMinute}
                  onSelectOpening={onSelectOpening}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* A gate, not a dropdown. There is no board behind it to go back to, so
          `onClose` is deliberately inert: the backdrop and Escape lead nowhere,
          and the way out is the Action Center link inside it. */}
      {asking && !loadingLocations && (
        <Modal
          useRadix
          open
          isFullScreen={false}
          closable={false}
          title="Which practice?"
          panelClassName="min-w-0 sm:max-w-md"
          onClose={() => {}}
        >
          <div className="flex flex-col gap-1.5">
            <p className="mb-1 text-body-sm text-subtle">
              This workspace runs more than one practice, and the board draws one at a time.
            </p>
            {locations.length > MAX_PRACTICE_BUTTONS_BEFORE_DROPDOWN ? (
              <select
                aria-label="Choose a practice"
                defaultValue=""
                onChange={(event) => onLocation(event.target.value)}
                className="rounded-lg border border-default bg-surface px-3 py-2.5 text-ui-sm text-strong"
              >
                {/* The placeholder has to be a real option: with a practice
                    preselected, picking the one at the top of the list fires no
                    change event and the board never draws. */}
                <option value="" disabled>
                  {locations.length} practices
                </option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            ) : (
              locations.map((location) => (
                <Button
                  key={location.id}
                  type="button"
                  variant="white"
                  size="lg"
                  onClick={() => onLocation(location.id)}
                  // oxlint-disable-next-line shadcn/no-restyle, shadcn/no-raw-colors -- emerald hover matches the schedule's open-slot color
                  className="justify-start rounded-lg px-3 text-ui-sm text-strong hover:border-emerald-300 hover:bg-emerald-50"
                >
                  {location.name}
                </Button>
              ))
            )}
            <Link to="/action-center" className="mt-2 text-body-sm text-subtle transition hover:text-strong">
              Back to Action Center
            </Link>
          </div>
        </Modal>
      )}
    </PageScroll>
  )
}
